/**
 * 已发布工具页面
 * 管理员专用 - 查看和管理所有已发布的工具
 */

import { useState, useEffect } from "react";
import { LayoutWithSidebar } from "@/components/LayoutWithSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, Loader2, Users, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toolsAPIV2, type Tool } from "@/services/toolsApiV2";
import { useAuthStore } from "@/stores/auth";
import { toast } from "sonner";

export function PublishedToolsPage() {
  const { accessToken } = useAuthStore();
  const [publishedTools, setPublishedTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(false);
  const [unpublishing, setUnpublishing] = useState<string | null>(null);
  const [searchKeyword, setSearchKeyword] = useState("");

  const loadPublishedTools = async () => {
    if (!accessToken) return;
    
    setLoading(true);
    try {
      const response = await toolsAPIV2.getPublishedTools(
        { page: 1, page_size: 100 },
        accessToken
      );
      
      if (response.success && response.data) {
        setPublishedTools(response.data.data);
      }
    } catch (error: any) {
      console.error('加载已发布工具失败:', error);
      toast.error('加载失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPublishedTools();
  }, []);

  const handleUnpublish = async (toolName: string) => {
    if (!accessToken) return;

    setUnpublishing(toolName);
    try {
      const response = await toolsAPIV2.unpublishToolByName(toolName, accessToken);
      if (response.success) {
        toast.success('已取消发布');
        await loadPublishedTools();
      }
    } catch (error: any) {
      toast.error('取消发布失败: ' + error.message);
    } finally {
      setUnpublishing(null);
    }
  };

  // 过滤工具
  const filteredTools = publishedTools.filter((tool) => {
    if (!searchKeyword) return true;
    const keyword = searchKeyword.toLowerCase();
    return (
      tool.name?.toLowerCase().includes(keyword) ||
      tool.display_name?.toLowerCase().includes(keyword) ||
      tool.description?.toLowerCase().includes(keyword) ||
      tool.server_name?.toLowerCase().includes(keyword) ||
      tool.category?.toLowerCase().includes(keyword)
    );
  });

  return (
    <LayoutWithSidebar title="已发布工具">
      <div className="flex flex-1 flex-col p-4">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 w-full">
            
            {/* 搜索框 */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="搜索工具名称、描述、分类..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="text-sm text-gray-500">
                  共 {filteredTools.length} 个工具
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : filteredTools.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <Package className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                  {searchKeyword ? (
                    <>
                      <p>未找到匹配的工具</p>
                      <p className="text-sm mt-2">试试其他搜索关键词</p>
                    </>
                  ) : (
                    <>
                      <p>还没有已发布的工具</p>
                      <p className="text-sm mt-2">在"MCP管理"页面中点击"发布"按钮来发布工具</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTools.map((tool) => (
                    <Card key={tool.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <Package className="h-8 w-8 text-green-500 mb-2" />
                          <Badge variant="default" className="bg-green-100 text-green-800">
                            已发布
                          </Badge>
                        </div>
                        <CardTitle className="text-xl">{tool.display_name || tool.name}</CardTitle>
                        <CardDescription className="line-clamp-2">
                          {tool.description || '暂无描述'}
                        </CardDescription>
                      </CardHeader>

                      <CardContent>
                        <div className="space-y-3">
                          <div className="text-sm">
                            <span className="font-medium text-gray-600">服务器:</span>{' '}
                            <Badge variant="outline">{tool.server_name}</Badge>
                          </div>
                          
                          {tool.category && (
                            <div className="text-sm">
                              <span className="font-medium text-gray-600">分类:</span>{' '}
                              <Badge variant="secondary">{tool.category}</Badge>
                            </div>
                          )}

                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="h-4 w-4 mr-2" />
                            <span>{tool.subscription_count || 0} 人订阅</span>
                          </div>

                          <Button
                            variant="outline"
                            className="w-full mt-4"
                            onClick={() => handleUnpublish(tool.name)}
                            disabled={unpublishing === tool.name}
                          >
                            {unpublishing === tool.name && (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            取消发布
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
            )}
          </div>
        </div>
      </div>
    </LayoutWithSidebar>
  );
}

export default PublishedToolsPage;

